import { Organisation, UpdateOrganisationData } from "../domain/types/organisation.types";
import { httpService } from "./http.service";
import { API_ENDPOINTS } from "../config/api.config";

export class OrganisationService {
  /**
   * Fetch organisation data by name
   * @param organisationName - The name of the organisation
   * @returns Promise<Organisation> - Organisation information
   */
  static async getOrganisation(organisationName: string): Promise<Organisation> {
    try {
      const response = await httpService.get<{
        id: string;
        name: string;
        address: string;
        phoneNumber: string;
        logoUrl?: string;
        createdAt: string;
        updatedAt: string;
      }>(API_ENDPOINTS.organisations.get(organisationName));

      // Transform API response to Organisation format
      return {
        id: response.id,
        name: response.name,
        address: response.address,
        phoneNumber: response.phoneNumber,
        logoUrl: response.logoUrl,
        createdAt: new Date(response.createdAt),
        updatedAt: new Date(response.updatedAt),
      };
    } catch (error) {
      console.error("Failed to fetch organisation:", error);
      throw error;
    }
  }

  /**
   * Update organisation data
   * @param organisationId - The ID of the organisation to update
   * @param organisationData - The organisation data to update
   * @returns Promise<Organisation> - Updated organisation information
   */
  static async updateOrganisation(
    organisationId: string,
    organisationData: UpdateOrganisationData
  ): Promise<Organisation> {
    try {
      const response = await httpService.patch<{
        id: string;
        name: string;
        address: string;
        phoneNumber: string;
        logoUrl?: string;
        createdAt: string;
        updatedAt: string;
      }>(API_ENDPOINTS.organisations.update(organisationId), organisationData);

      // Transform API response to Organisation format
      return {
        id: response.id,
        name: response.name,
        address: response.address,
        phoneNumber: response.phoneNumber,
        logoUrl: response.logoUrl,
        createdAt: new Date(response.createdAt),
        updatedAt: new Date(response.updatedAt),
      };
    } catch (error) {
      console.error("Failed to update organisation:", error);
      throw error;
    }
  }

  /**
   * Upload logo for organisation
   * @param organisationId - The ID of the organisation
   * @param logoFile - The logo image file to upload
   * @returns Promise<{ url: string }> - URL of the uploaded logo
   */
  static async uploadLogo(organisationId: string, logoFile: File): Promise<{ url: string }> {
    try {
      const formData = new FormData();
      formData.append("file", logoFile);

      const response = await httpService.post<{ url: string }>(
        `/api/organisations/logo/${organisationId}`,
        formData
      );

      return response;
    } catch (error) {
      console.error("Failed to upload logo:", error);
      throw error;
    }
  }

  /**
   * Store organisation data in localStorage (for caching)
   * @param organisation - Organisation data to store
   */
  static saveOrganisation(organisation: Organisation): void {
    localStorage.setItem("organisationData", JSON.stringify(organisation));
  }

  /**
   * Get organisation data from localStorage (cache)
   * @returns Organisation | null - Stored organisation data or null if not found
   */
  static getOrganisationFromStorage(): Organisation | null {
    const orgStr = localStorage.getItem("organisationData");
    return orgStr ? JSON.parse(orgStr) : null;
  }

  /**
   * Clear organisation data from localStorage
   */
  static clearOrganisation(): void {
    localStorage.removeItem("organisationData");
  }

  static async deleteOrganisation(orgId: string): Promise<void> {
    await httpService.delete(`/api/organisations/${orgId}`);
  }
}
